import React from "react";
import {
  HeartHandshake,
  ShieldCheck,
  Baby,
  HousePlus,
  Users,
  MapPinnedIcon,
} from "lucide-react";
import Family from '../../assets/family-img.jpg'
import { motion } from "framer-motion";

const EmployeeSupport = () => {
  const supportServices = [
    {
      title: "Nursing Support",
      icon: <HeartHandshake size={24} className="text-royalBlue" strokeWidth={1.5} />,
    },
    {
      title: "Homecare Assistance",
      icon: <HousePlus size={24} className="text-royalGreen" strokeWidth={1.5} />,
    },
    {
      title: "Babysitting Services",
      icon: <Baby size={24} className="text-royalBlue" strokeWidth={1.5} />,
    },
    {
      title: "Trusted Coordination",
      icon: <ShieldCheck size={24} className="text-royalGreen" strokeWidth={1.5} />,
    },
  ];

  return (
    <motion.section
      id="EmployeeSupport"
      initial={{ opacity: 0, x: -40 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{
        duration: 0.8,
        ease: "easeIn"
      }}
      viewport={{ once: true, amount: 0.3 }}
      className="bg-gradient-to-b from-secondary to-secondary/80 py-[6vw] lg:py-[3vw] overflow-hidden relative"
    >
      {/* Background Effects */}
      <div className="absolute top-[10%] left-[-5%] w-[180px] h-[180px] lg:w-[250px] lg:h-[250px] bg-royalBlue/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-[0%] right-[-5%] w-[180px] h-[180px] lg:w-[250px] lg:h-[250px] bg-royalGreen/5 rounded-full blur-3xl"></div>

      <div className="max-w-[1450px] mx-auto px-[5%] relative z-10">
        
        {/* New Layout: Pill heading first, then content, then image */}
        <div className="flex flex-col items-center gap-[2vw] lg:gap-[1.75vw]">
        {/* Pill Header - Copy this block, change the text only */}
<div className="flex justify-center mb-6 md:mb-10 px-4">
  <div className="inline-flex items-center gap-2 bg-gradient-to-r from-royalBlue/10 to-royalBlue/5 backdrop-blur-sm rounded-full px-4 py-1.5 sm:px-5 md:px-7 sm:py-2 md:py-3 border border-royalBlue/20 shadow-lg">
    <MapPinnedIcon size={16} className="text-royalGreen sm:size-[18px] flex-shrink-0" />
    <p className="font-poppins font-semibold text-royalBlue text-[11px] xs:text-xs sm:text-sm md:text-base lg:text-lg tracking-wide uppercase whitespace-nowrap">
      {/* Full text on medium screens and above */}
      <span className="hidden lg:inline">SUPPORT FOR EMPLOYEES FAR FROM HOME</span>
      
      {/* Medium text on small screens (shorter version) */}
      <span className="xs:inline md:inline lg:hidden">SUPPORT FOR EMPLOYEES</span>
      
      {/* Shortest text on very small screens (320px) */}
      <span className="hidden xs:inline">SUPPORT FOR EMPLOYEES</span>
    </p>
  </div>
</div>

          {/* Content row: text + image (stack on mobile, but image after text) */}
          <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-[4vw] lg:gap-[2.5vw]">
            
            {/* Left Content (text, cards, badge) */}
            <div className="w-full lg:w-[52%] order-1">
              <h2 className="mt-2.5 font-poppins font-[700] text-darkblue leading-[1] text-[5vw] sm:text-[3.5vw] md:text-[2vw] lg:text-[2.8vw] bg-gradient-to-r from-darkblue to-primary bg-clip-text text-transparent">
                Support When Employees Are Away From Home
              </h2>

              <p className="mt-2.5 text-text1/90 leading-[1.7] font-poppins text-[3.5vw] sm:text-[2.2vw] md:text-[1.4vw] lg:text-[1.02vw]">
                Many employees work away from their hometowns. During health
                emergencies or medical needs, it can be stressful to find
                reliable support.
              </p>

              <p className="mt-2 text-text1/90 leading-[1.7] font-poppins text-[3.5vw] sm:text-[2.2vw] md:text-[1.4vw] lg:text-[1.02vw]">
                Livmo helps employees by coordinating trusted healthcare and
                homecare services including nursing support, homecare assistance,
                and babysitting services when required.
              </p>

              {/* Support Cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-[4vw] lg:mt-[2vw]">
                {supportServices.map((service, index) => (
                  <div
                    key={index}
                    className="group bg-white border border-gray-100 hover:border-royalBlue/20 rounded-xl lg:rounded-2xl p-4 shadow-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex items-center gap-3 lg:gap-4"
                  >
                    <div className="min-w-[48px] min-h-[48px] lg:min-w-[55px] lg:min-h-[55px] rounded-xl lg:rounded-2xl bg-gradient-to-br from-royalBlue/10 to-royalGreen/10 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      {service.icon}
                    </div>

                    <h3 className="font-poppins font-[600] text-darkblue leading-[1.4] text-[3.8vw] sm:text-[2.2vw] md:text-[1.3vw] lg:text-[1vw] group-hover:text-royalBlue transition-colors duration-300">
                      {service.title}
                    </h3>
                  </div>
                ))}
              </div>

              {/* Bottom Badge */}
              <div className="mt-[4vw] lg:mt-[2vw] inline-flex items-center gap-2 lg:gap-3 bg-gradient-to-r from-royalBlue to-royalGreen px-5 py-3 lg:px-6 lg:py-4 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300">
                <Users size={18} className="text-white lg:size-[22px]" />
                <p className="text-white font-poppins font-[600] text-[3vw] sm:text-[1.8vw] md:text-[1.1vw] lg:text-[0.9vw]">
                  Reliable Healthcare Coordination for Employees Across India
                </p>
              </div>
            </div>

            {/* Right Image - order after text, smaller on mobile */}
            <div className="w-full lg:w-[48%] flex justify-center order-2 mt-2 lg:mt-0">
              <div className="relative w-full max-w-[350px] sm:max-w-[450px] lg:max-w-[650px]">
                {/* Glow */}
                <div className="absolute inset-0 bg-gradient-to-r from-royalBlue/15 to-royalGreen/15 blur-3xl rounded-[4vw]"></div>

                {/* Main Image Card */}
                <div className="relative bg-white p-3 lg:p-4 rounded-2xl lg:rounded-[2.5vw] shadow-2xl border border-gray-100 overflow-hidden">
                  <img
                    src={Family}
                    alt="Employee healthcare support"
                    className="w-full h-[240px] sm:h-[280px] lg:h-[480px] object-cover rounded-xl lg:rounded-[2vw]"
                  />

                  {/* Floating Card - centered at bottom */}
                  <div className="absolute bottom-[5%] left-1/2 transform -translate-x-1/2 bg-white/95 backdrop-blur-md shadow-xl border border-gray-100 rounded-xl lg:rounded-2xl p-3 lg:px-5 lg:py-4 flex items-center gap-3 lg:gap-4 w-[90%] lg:w-[85%] max-w-[320px] lg:max-w-[380px]">
                    <div className="min-w-[42px] min-h-[42px] lg:w-[50px] lg:h-[50px] rounded-xl lg:rounded-2xl bg-gradient-to-r from-royalBlue to-royalGreen flex items-center justify-center">
                      <HeartHandshake size={18} className="text-white lg:size-[22px]" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-poppins font-[700] text-darkblue text-[3.2vw] sm:text-[2vw] md:text-[1.1vw] lg:text-[0.9vw]">
                        Trusted Healthcare Support
                      </h4>
                      <p className="text-text1/80 mt-0.5 lg:mt-1 leading-[1.4] font-poppins text-[2.8vw] sm:text-[1.6vw] md:text-[0.9vw] lg:text-[0.75vw]">
                        Helping employees access care and support when family is far away.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.section>
  );
};

export default EmployeeSupport;